import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import { SCREEN_WIDTH } from '../../utils/screensize';
import type {
  ScheduleFilterScheduleType,
  ScheduleFilterState,
  ScheduleFilterStatus,
  ScheduleFilterType,
  ScheduleSortDirection,
  ScheduleSortField
} from '../../typing/scheduleFilter';
import { DEFAULT_FILTER_STATE } from '../../typing/scheduleFilter';

type SiteOption = { id: string; name: string };
type FilterCategory = 'site' | 'status' | 'reportType' | 'scheduleType';
type ExpandedSection = FilterCategory | 'sort' | null;
type ChipCategory = 'site' | 'status' | 'type' | 'scheduleType';

type FilterChip = { key: string; label: string; onRemove: () => void };

type RowDescriptor = {
  key: FilterCategory;
  title: string;
  icon: string;
  iconTint: string;
  iconBg: string;
  subtitle: string;
  renderBody: () => React.ReactNode;
};

// Match the app's centered frame width (see routes.tsx / HomeCheckInModal)
const FRAME_WIDTH = SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH;

interface Props {
  visible: boolean;
  initialFilter: ScheduleFilterState;
  availableSites: SiteOption[];
  onApply: (filter: ScheduleFilterState) => void;
  onClose: () => void;
}

const STATUS_OPTIONS: { value: ScheduleFilterStatus; label: string }[] = [
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'OVERDUE', label: 'Overdue' }
];

const REPORT_TYPE_OPTIONS: { value: ScheduleFilterType; label: string }[] = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'ADHOC', label: 'Adhoc' }
];

const SCHEDULE_TYPE_OPTIONS: { value: ScheduleFilterScheduleType; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'team', label: 'Team' }
];

const SORT_FIELD_OPTIONS: { value: ScheduleSortField; label: string }[] = [
  { value: 'siteName', label: 'Site Name' },
  { value: 'status', label: 'Status' },
  { value: 'type', label: 'Report Type' }
];

// Tinted icon palette per filter category (tint / background)
const ICON_THEME = {
  site: { tint: '#009e43', bg: '#E7F8EE' },
  status: { tint: '#475467', bg: '#F2F4F7' },
  reportType: { tint: '#175CD3', bg: '#EFF4FF' },
  scheduleType: { tint: '#B54708', bg: '#FEF6EE' },
  sort: { tint: '#009e43', bg: '#E7F8EE' }
};

function toggleItem<T>(arr: T[], item: T): T[] {
  return arr.includes(item) ? arr.filter(v => v !== item) : [...arr, item];
}

function labelMapOf(options: { value: string; label: string }[]): Record<string, string> {
  const map: Record<string, string> = {};
  options.forEach(o => {
    map[o.value] = o.label;
  });
  return map;
}

function selectionSummary(values: string[], resolveLabel: (value: string) => string): string {
  if (values.length === 0) {
    return 'All';
  }
  if (values.length === 1) {
    return resolveLabel(values[0]);
  }
  return `${values.length} selected`;
}

function removeValue(filter: ScheduleFilterState, category: ChipCategory, value: string): ScheduleFilterState {
  switch (category) {
    case 'site':
      return { ...filter, sites: filter.sites.filter(v => v !== value) };
    case 'status':
      return { ...filter, statuses: filter.statuses.filter(v => v !== value) };
    case 'type':
      return { ...filter, types: filter.types.filter(v => v !== value) };
    case 'scheduleType':
      return { ...filter, scheduleTypes: filter.scheduleTypes.filter(v => v !== value) };
    default:
      return filter;
  }
}

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text style={styles.sectionLabel}>{children}</Text>
);

const TintedIcon: React.FC<{ name: string; tint: string; bg: string; size?: number }> = ({
  name,
  tint,
  bg,
  size = 20
}) => (
  <View style={[styles.iconSquare, { backgroundColor: bg }]}>
    <Icon name={name} size={size} color={tint} />
  </View>
);

const Chip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <View style={styles.chip}>
    <Icon name="filter-variant" size={13} color="#027A48" style={styles.chipIcon} />
    <Text style={styles.chipText} numberOfLines={1}>
      {label}
    </Text>
    <TouchableOpacity
      onPress={onRemove}
      style={styles.chipClose}
      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
      activeOpacity={0.7}
    >
      <Icon name="close" size={14} color="#027A48" />
    </TouchableOpacity>
  </View>
);

const ActiveFilterChips: React.FC<{ chips: FilterChip[] }> = ({ chips }) => {
  if (chips.length === 0) {
    return null;
  }
  return (
    <>
      <SectionLabel>Active Filters</SectionLabel>
      <View style={styles.chipRow}>
        {chips.map(chip => (
          <Chip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
        ))}
      </View>
    </>
  );
};

const FilterCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.card}>{children}</View>
);

interface FilterRowProps {
  icon: string;
  iconTint: string;
  iconBg: string;
  title: string;
  subtitle: string;
  isExpanded: boolean;
  onPress: () => void;
}

const FilterRow: React.FC<FilterRowProps> = ({ icon, iconTint, iconBg, title, subtitle, isExpanded, onPress }) => (
  <TouchableOpacity style={styles.rowHeader} onPress={onPress} activeOpacity={0.7}>
    <TintedIcon name={icon} tint={iconTint} bg={iconBg} />
    <View style={styles.rowTextWrap}>
      <Text style={styles.rowTitle}>{title}</Text>
      <Text style={styles.rowSubtitle} numberOfLines={1}>
        {subtitle}
      </Text>
    </View>
    <Icon name={isExpanded ? 'chevron-up' : 'chevron-right'} size={22} color="#98A2B3" />
  </TouchableOpacity>
);

interface CheckRowProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
}

const CheckRow: React.FC<CheckRowProps> = ({ label, checked, onToggle }) => (
  <TouchableOpacity style={styles.checkRow} onPress={onToggle} activeOpacity={0.7}>
    <Icon
      name={checked ? 'checkbox-marked' : 'checkbox-blank-outline'}
      size={20}
      color={checked ? colors.primary : '#C4CDD9'}
      style={styles.checkRowIcon}
    />
    <Text style={[styles.checkRowLabel, checked ? styles.checkRowLabelActive : null]}>{label}</Text>
  </TouchableOpacity>
);

interface SortRowProps {
  fieldLabel: string;
  directionLabel: string;
  direction: ScheduleSortDirection;
  isExpanded: boolean;
  onToggleDirection: () => void;
  onPressExpand: () => void;
}

const SortRow: React.FC<SortRowProps> = ({
  fieldLabel,
  directionLabel,
  direction,
  isExpanded,
  onToggleDirection,
  onPressExpand
}) => (
  <View style={styles.rowHeader}>
    <TouchableOpacity style={styles.rowMain} onPress={onPressExpand} activeOpacity={0.7}>
      <TintedIcon name="sort-variant" tint={ICON_THEME.sort.tint} bg={ICON_THEME.sort.bg} />
      <View style={styles.rowTextWrap}>
        <Text style={styles.rowTitle}>{fieldLabel}</Text>
        <Text style={styles.rowSubtitle} numberOfLines={1}>
          {directionLabel}
        </Text>
      </View>
    </TouchableOpacity>
    <TouchableOpacity
      onPress={onToggleDirection}
      style={styles.sortDirBtn}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      activeOpacity={0.7}
    >
      <Icon name={direction === 'asc' ? 'arrow-up' : 'arrow-down'} size={18} color={colors.primary} />
    </TouchableOpacity>
    <TouchableOpacity style={styles.sortChevronBtn} onPress={onPressExpand} activeOpacity={0.7}>
      <Icon name={isExpanded ? 'chevron-up' : 'chevron-right'} size={22} color="#98A2B3" />
    </TouchableOpacity>
  </View>
);

const ScheduleFilterModal: React.FC<Props> = ({ visible, initialFilter, availableSites, onApply, onClose }) => {
  const [draft, setDraft] = useState<ScheduleFilterState>(initialFilter);
  const [expanded, setExpanded] = useState<ExpandedSection>(null);
  const [siteSearch, setSiteSearch] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft(initialFilter);
      setExpanded(null);
      setSiteSearch('');
    }
  }, [visible, initialFilter]);

  const toggleSection = (section: ExpandedSection) => {
    setExpanded(prev => (prev === section ? null : section));
  };

  const siteNameById = useMemo(() => {
    const map: Record<string, string> = {};
    availableSites.forEach(s => {
      map[s.id] = s.name;
    });
    return map;
  }, [availableSites]);

  const statusLabels = useMemo(() => labelMapOf(STATUS_OPTIONS), []);
  const typeLabels = useMemo(() => labelMapOf(REPORT_TYPE_OPTIONS), []);
  const scheduleTypeLabels = useMemo(() => labelMapOf(SCHEDULE_TYPE_OPTIONS), []);

  const resolveSiteName = (id: string) => siteNameById[id] ?? id;

  const filteredSites = siteSearch.trim()
    ? availableSites.filter(s => s.name.toLowerCase().includes(siteSearch.toLowerCase()))
    : availableSites;

  const chips: FilterChip[] = useMemo(() => {
    const result: FilterChip[] = [];
    draft.sites.forEach(id => {
      result.push({
        key: `site:${id}`,
        label: `Site: ${siteNameById[id] ?? id}`,
        onRemove: () => setDraft(d => removeValue(d, 'site', id))
      });
    });
    draft.statuses.forEach(value => {
      result.push({
        key: `status:${value}`,
        label: `Status: ${statusLabels[value] ?? value}`,
        onRemove: () => setDraft(d => removeValue(d, 'status', value))
      });
    });
    draft.types.forEach(value => {
      result.push({
        key: `type:${value}`,
        label: `Report Type: ${typeLabels[value] ?? value}`,
        onRemove: () => setDraft(d => removeValue(d, 'type', value))
      });
    });
    draft.scheduleTypes.forEach(value => {
      result.push({
        key: `scheduleType:${value}`,
        label: `Schedule Type: ${scheduleTypeLabels[value] ?? value}`,
        onRemove: () => setDraft(d => removeValue(d, 'scheduleType', value))
      });
    });
    return result;
  }, [draft, siteNameById, statusLabels, typeLabels, scheduleTypeLabels]);

  function renderOptionsBody<T extends string>(
    options: { value: T; label: string }[],
    selected: T[],
    onToggleValue: (value: T) => void
  ) {
    return (
      <View style={styles.expandedBody}>
        {options.map(opt => (
          <CheckRow
            key={opt.value}
            label={opt.label}
            checked={selected.includes(opt.value)}
            onToggle={() => onToggleValue(opt.value)}
          />
        ))}
      </View>
    );
  }

  const renderSiteBody = () => (
    <View style={styles.expandedBody}>
      <View style={styles.searchWrap}>
        <Icon name="magnify" size={18} color="#98A2B3" style={styles.searchIcon} />
        <TextInput
          style={styles.siteSearch}
          placeholder="Search site..."
          placeholderTextColor="#B2B8C2"
          value={siteSearch}
          onChangeText={setSiteSearch}
        />
      </View>
      {filteredSites.length === 0 ? (
        <Text style={styles.emptySubText}>No sites available</Text>
      ) : (
        <ScrollView style={styles.checkList} nestedScrollEnabled keyboardShouldPersistTaps="handled">
          {filteredSites.map(site => (
            <CheckRow
              key={site.id}
              label={site.name}
              checked={draft.sites.includes(site.id)}
              onToggle={() => setDraft(d => ({ ...d, sites: toggleItem(d.sites, site.id) }))}
            />
          ))}
        </ScrollView>
      )}
      <View style={styles.catFooter}>
        <TouchableOpacity onPress={() => setDraft(d => ({ ...d, sites: [] }))} activeOpacity={0.7}>
          <Text style={styles.catClear}>Clear</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setExpanded(null)} activeOpacity={0.7}>
          <Text style={styles.catDone}>Done</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const filterRows: RowDescriptor[] = [
    {
      key: 'site',
      title: 'Site',
      icon: 'office-building',
      iconTint: ICON_THEME.site.tint,
      iconBg: ICON_THEME.site.bg,
      subtitle: selectionSummary(draft.sites, resolveSiteName),
      renderBody: renderSiteBody
    },
    {
      key: 'status',
      title: 'Status',
      icon: 'clipboard-text-outline',
      iconTint: ICON_THEME.status.tint,
      iconBg: ICON_THEME.status.bg,
      subtitle: selectionSummary(draft.statuses, v => statusLabels[v] ?? v),
      renderBody: () =>
        renderOptionsBody(STATUS_OPTIONS, draft.statuses, value =>
          setDraft(d => ({ ...d, statuses: toggleItem(d.statuses, value) }))
        )
    },
    {
      key: 'reportType',
      title: 'Report Type',
      icon: 'file-document-outline',
      iconTint: ICON_THEME.reportType.tint,
      iconBg: ICON_THEME.reportType.bg,
      subtitle: selectionSummary(draft.types, v => typeLabels[v] ?? v),
      renderBody: () =>
        renderOptionsBody(REPORT_TYPE_OPTIONS, draft.types, value =>
          setDraft(d => ({ ...d, types: toggleItem(d.types, value) }))
        )
    },
    {
      key: 'scheduleType',
      title: 'Schedule Type',
      icon: 'calendar-blank-outline',
      iconTint: ICON_THEME.scheduleType.tint,
      iconBg: ICON_THEME.scheduleType.bg,
      subtitle: selectionSummary(draft.scheduleTypes, v => scheduleTypeLabels[v] ?? v),
      renderBody: () =>
        renderOptionsBody(SCHEDULE_TYPE_OPTIONS, draft.scheduleTypes, value =>
          setDraft(d => ({ ...d, scheduleTypes: toggleItem(d.scheduleTypes, value) }))
        )
    }
  ];

  const renderFilterCards = () => {
    type Group = { type: 'collapsed'; rows: RowDescriptor[] } | { type: 'expanded'; row: RowDescriptor };
    const groups: Group[] = [];
    let run: RowDescriptor[] = [];
    filterRows.forEach(row => {
      if (row.key === expanded) {
        if (run.length) {
          groups.push({ type: 'collapsed', rows: run });
          run = [];
        }
        groups.push({ type: 'expanded', row });
      } else {
        run.push(row);
      }
    });
    if (run.length) {
      groups.push({ type: 'collapsed', rows: run });
    }

    return groups.map((group, gi) => {
      if (group.type === 'expanded') {
        const { row } = group;
        return (
          <FilterCard key={row.key}>
            <FilterRow
              icon={row.icon}
              iconTint={row.iconTint}
              iconBg={row.iconBg}
              title={row.title}
              subtitle={row.subtitle}
              isExpanded
              onPress={() => toggleSection(row.key)}
            />
            {row.renderBody()}
          </FilterCard>
        );
      }
      return (
        <FilterCard key={`grp-${gi}-${group.rows[0].key}`}>
          {group.rows.map((row, i) => (
            <React.Fragment key={row.key}>
              {i > 0 && <View style={styles.cardDivider} />}
              <FilterRow
                icon={row.icon}
                iconTint={row.iconTint}
                iconBg={row.iconBg}
                title={row.title}
                subtitle={row.subtitle}
                isExpanded={false}
                onPress={() => toggleSection(row.key)}
              />
            </React.Fragment>
          ))}
        </FilterCard>
      );
    });
  };

  const sortFieldLabel = SORT_FIELD_OPTIONS.find(o => o.value === draft.sortField)?.label ?? '';
  const directionLabel = draft.sortDirection === 'asc' ? 'Ascending' : 'Descending';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.root}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.backButton} activeOpacity={0.7}>
            <Icon name="arrow-left" size={22} color="#25282B" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Filter & Sort</Text>
          <View style={styles.headerRight} />
        </View>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Active filters */}
          <ActiveFilterChips chips={chips} />

          {/* Filters */}
          <SectionLabel>Filters</SectionLabel>
          {renderFilterCards()}

          {/* Sort by */}
          <SectionLabel>Sort by</SectionLabel>
          <FilterCard>
            <SortRow
              fieldLabel={sortFieldLabel}
              directionLabel={directionLabel}
              direction={draft.sortDirection}
              isExpanded={expanded === 'sort'}
              onToggleDirection={() =>
                setDraft(d => ({ ...d, sortDirection: d.sortDirection === 'asc' ? 'desc' : 'asc' }))
              }
              onPressExpand={() => toggleSection('sort')}
            />
            {expanded === 'sort' && (
              <View style={styles.expandedBody}>
                {SORT_FIELD_OPTIONS.map(opt => (
                  <TouchableOpacity
                    key={opt.value}
                    style={styles.pickerRow}
                    onPress={() => {
                      setDraft(d => ({ ...d, sortField: opt.value }));
                      setExpanded(null);
                    }}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.checkRowLabel,
                        draft.sortField === opt.value ? styles.checkRowLabelActive : null
                      ]}
                    >
                      {opt.label}
                    </Text>
                    {draft.sortField === opt.value && <Icon name="check" size={18} color={colors.primary} />}
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </FilterCard>
        </ScrollView>

        {/* Footer */}
        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.resetButton}
            onPress={() => {
              setDraft(DEFAULT_FILTER_STATE);
              setExpanded(null);
              setSiteSearch('');
            }}
            activeOpacity={0.7}
          >
            <Text style={styles.resetLabel}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.applyButton} onPress={() => onApply(draft)} activeOpacity={0.8}>
            <Text style={styles.applyLabel}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default ScheduleFilterModal;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: FRAME_WIDTH,
    marginHorizontal: 'auto',
    backgroundColor: '#F4F6F8',
    flexDirection: 'column'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E9EDF3'
  },
  backButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTitle: {
    ...fontMaker({ weight: 'SemiBold' }),
    flex: 1,
    textAlign: 'center',
    fontSize: 17,
    color: '#25282B'
  },
  headerRight: {
    width: 36
  },
  scroll: {
    flex: 1,
    minHeight: 0 as any
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 24
  },
  sectionLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 12,
    color: '#98A2B3',
    marginTop: 18,
    marginBottom: 8,
    marginLeft: 4
  },
  // Active filter chips
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#ECFDF3',
    borderRadius: 16,
    paddingLeft: 10,
    paddingRight: 6,
    paddingVertical: 5,
    marginRight: 8,
    marginBottom: 8
  },
  chipIcon: {
    marginRight: 5
  },
  chipText: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: '#027A48',
    maxWidth: 220
  },
  chipClose: {
    marginLeft: 4,
    padding: 2
  },
  // Cards
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#E9EDF3',
    marginBottom: 12,
    overflow: 'hidden',
    shadowColor: '#0F172A',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#F0F2F5',
    marginLeft: 62
  },
  // Row header
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 14
  },
  rowMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center'
  },
  iconSquare: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12
  },
  rowTextWrap: {
    flex: 1
  },
  rowTitle: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: '#25282B'
  },
  rowSubtitle: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: '#667085',
    marginTop: 2
  },
  // Expanded body
  expandedBody: {
    borderTopWidth: 1,
    borderTopColor: '#F0F2F5',
    paddingHorizontal: 14,
    paddingTop: 4,
    paddingBottom: 10
  },
  // Site search
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E9EDF3',
    paddingHorizontal: 10,
    marginTop: 10,
    marginBottom: 4
  },
  searchIcon: {
    marginRight: 6
  },
  siteSearch: {
    ...fontMaker({ weight: 'Normal' }),
    flex: 1,
    height: 38,
    fontSize: 14,
    color: '#25282B'
  },
  checkList: {
    maxHeight: 240
  },
  emptySubText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: colors.gray,
    paddingVertical: 12,
    textAlign: 'center'
  },
  // Checkbox rows
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11
  },
  checkRowIcon: {
    marginRight: 10
  },
  checkRowLabel: {
    ...fontMaker({ weight: 'Normal' }),
    flex: 1,
    fontSize: 14,
    color: '#667085'
  },
  checkRowLabelActive: {
    ...fontMaker({ weight: 'SemiBold' }),
    color: '#25282B'
  },
  // Sort field picker rows
  pickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12
  },
  // Category footer (Clear / Done)
  catFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 10,
    marginTop: 2,
    borderTopWidth: 1,
    borderTopColor: '#F0F2F5'
  },
  catClear: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: '#667085'
  },
  catDone: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 13,
    color: colors.primaryDark
  },
  // Sort row controls
  sortDirBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 2
  },
  sortChevronBtn: {
    width: 28,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center'
  },
  // Footer
  footer: {
    flexShrink: 0 as any,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: '#E9EDF3',
    backgroundColor: '#fff',
    gap: 12
  },
  resetButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center'
  },
  resetLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: colors.primary
  },
  applyButton: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center'
  },
  applyLabel: {
    ...fontMaker({ weight: 'SemiBold' }),
    fontSize: 15,
    color: '#fff'
  }
});
